import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import api, { brl } from "../lib/api";
import { openPdf, openHtmlDoc, printReceipt80mm } from "../lib/fiscal";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, ShoppingCart, X, ExternalLink, Copy, Loader, FileText, Receipt, Printer, ScanLine } from "lucide-react";

// Beep de confirmação via WebAudio (sem arquivo).
function playBeep(ok = true) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = ok ? 880 : 220;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.13);
    setTimeout(() => ctx.close && ctx.close(), 200);
  } catch (e) {
    console.debug("beep unsupported:", e);
  }
}

// Helpers de estilo (evitam ternárias aninhadas no JSX)
function barcodeIconColor(flash) {
  if (flash === "error") return "text-red-500";
  if (flash === "ok") return "text-emerald-500";
  return "text-[#2563EB]";
}
function barcodeInputBorder(flash) {
  if (flash === "error") return "border-red-500 ring-2 ring-red-300 animate-pulse";
  if (flash === "ok") return "border-emerald-500";
  return "border-[#2563EB]/40";
}
function stockBadgeStyle(p) {
  if (p.stock <= 0) return "bg-red-100 text-red-700";
  if (p.stock <= (p.min_stock || 5)) return "bg-amber-100 text-amber-700";
  return "bg-emerald-100 text-emerald-700";
}

export default function PDV() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [payment, setPayment] = useState("dinheiro");
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [ipCheckout, setIpCheckout] = useState(null); // {order_id, checkout_url}
  const [ipPolling, setIpPolling] = useState(false);
  const [lastSale, setLastSale] = useState(null); // full sale doc { id, total, items, ... }
  const [emitting, setEmitting] = useState(false);
  const [pendingDanfe, setPendingDanfe] = useState(null); // { docType, ref, label }
  const [storeInfo, setStoreInfo] = useState({});
  const [barcode, setBarcode] = useState("");
  const [barcodeFlash, setBarcodeFlash] = useState(null); // 'ok' | 'error' | null
  const barcodeRef = useRef(null);

  const focusBarcode = useCallback(() => {
    // setTimeout garante que o input já esteja renderizado após state updates
    setTimeout(() => {
      if (barcodeRef.current && !ipCheckout && !lastSale) {
        barcodeRef.current.focus();
        barcodeRef.current.select?.();
      }
    }, 0);
  }, [ipCheckout, lastSale]);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch((e) => console.error("PDV load products:", e));
    api.get("/customers").then((r) => setCustomers(r.data)).catch((e) => console.error("PDV load customers:", e));
    api.get("/employees").then((r) => setEmployees(r.data)).catch((e) => console.error("PDV load employees:", e));
  }, []);
  useEffect(() => {
    load();
    api.get("/public/settings")
      .then((r) => setStoreInfo(r.data || {}))
      .catch((e) => console.error("PDV load settings:", e));
  }, [load]);

  // Autofoco no leitor de código de barras assim que os produtos carregam
  // e sempre que fechamos algum modal.
  useEffect(() => {
    if (!ipCheckout && !lastSale && products.length > 0) {
      focusBarcode();
    }
  }, [products.length, ipCheckout, lastSale, focusBarcode]);

  const filtered = useMemo(
    () => products.filter((p) =>
      p.name.toLowerCase().includes(q.toLowerCase()) ||
      (p.sku || "").toLowerCase().includes(q.toLowerCase())
    ),
    [products, q]
  );

  const add = (p) => {
    if (p.stock <= 0) { toast.error("Sem estoque"); return; }
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) return c.map((i) => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...c, { product_id: p.id, name: p.name, unit_price: p.sale_price, quantity: 1 }];
    });
    focusBarcode();
  };

  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    const code = (barcode || "").trim();
    if (!code) return;
    // Match exato (case-insensitive) por SKU/código de barras.
    const needle = code.toLowerCase();
    const hit = products.find((p) => (p.sku || "").toLowerCase() === needle);
    if (!hit) {
      playBeep(false);
      toast.error(`Produto não encontrado: ${code}`);
      setBarcodeFlash("error");
      setTimeout(() => setBarcodeFlash(null), 900);
      barcodeRef.current?.select?.();
      return;
    }
    if (hit.stock <= 0) {
      playBeep(false);
      toast.error(`${hit.name} — sem estoque`);
      setBarcodeFlash("error");
      setTimeout(() => setBarcodeFlash(null), 900);
      barcodeRef.current?.select?.();
      return;
    }
    add(hit);
    playBeep(true);
    setBarcodeFlash("ok");
    setTimeout(() => setBarcodeFlash(null), 400);
    setBarcode("");
    // focusBarcode() já é chamado dentro de add()
  };

  const changeQty = (pid, delta) => {
    setCart((c) => c.map((i) =>
      i.product_id === pid ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i
    ));
  };

  const removeItem = (pid) => setCart((c) => c.filter((i) => i.product_id !== pid));

  const subtotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const total = Math.max(0, subtotal - (Number(discount) || 0));

  useEffect(() => {
    if (coupon) setDiscount(Math.round(subtotal * coupon.percent) / 100);
  }, [coupon, subtotal]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.get(`/coupons/${encodeURIComponent(couponCode.trim())}/validate`);
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado: ${data.percent}% OFF (${data.customer_name})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cupom inválido"); }
  };
  const removeCoupon = () => { setCoupon(null); setCouponCode(""); setDiscount(0); };
  const redeemCoupon = async (saleId) => {
    if (!coupon) return;
    try { await api.post(`/coupons/${coupon.code}/redeem`, { sale_id: saleId || "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao baixar cupom"); }
    setCoupon(null); setCouponCode("");
  };

  const finalize = async () => {
    if (cart.length === 0) { toast.error("Carrinho vazio"); return; }
    // InfinitePay: create checkout link and wait for webhook confirmation
    if (payment === "infinitepay") {
      try {
        const { data } = await api.post("/infinitepay/checkout", {
          items: cart, customer_id: customerId, customer_name: customerName,
          employee_id: employeeId, discount: Number(discount) || 0,
        });
        setIpCheckout(data);
        setIpPolling(true);
        redeemCoupon(data?.order_id || data?.id);
        toast.info("Aguardando pagamento na maquininha / link...");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erro ao criar cobrança InfinitePay");
      }
      return;
    }
    try {
      const { data } = await api.post("/sales", {
        items: cart, customer_id: customerId, customer_name: customerName,
        employee_id: employeeId, payment_method: payment, discount: Number(discount) || 0,
      });
      toast.success("Venda finalizada!");
      await redeemCoupon(data.id);
      setLastSale(data);
      setPendingDanfe(null);
      setCart([]); setCustomerId(""); setCustomerName(""); setDiscount(0);
      load();
    } catch (e) {
      console.error("PDV finalize:", e);
      toast.error("Erro ao finalizar venda");
    }
  };

  // Poll InfinitePay payment status
  useEffect(() => {
    if (!ipPolling || !ipCheckout?.order_id) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/infinitepay/status/${ipCheckout.order_id}`);
        if (data.status === "paid") {
          clearInterval(interval);
          setIpPolling(false);
          setIpCheckout(null);
          if (data.sale_id) {
            // Buscar venda completa para poder imprimir cupom
            try {
              const sr = await api.get(`/sales`);
              const full = (sr.data || []).find((s) => s.id === data.sale_id);
              setLastSale(full || { id: data.sale_id, total: data.total, customer_name: data.customer_name });
            } catch (err) {
              console.error("PDV fetch sale:", err);
              setLastSale({ id: data.sale_id, total: data.total, customer_name: data.customer_name });
            }
            setPendingDanfe(null);
          }
          setCart([]); setCustomerId(""); setCustomerName(""); setDiscount(0);
          toast.success("Pagamento confirmado! Venda finalizada.");
          load();
        }
      } catch (err) {
        // Polling — logamos mas não interrompemos
        if (err?.response?.status && err.response.status >= 500) {
          console.error("PDV IP poll:", err);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [ipPolling, ipCheckout, load]);

  const cancelIpCheckout = () => {
    setIpCheckout(null); setIpPolling(false);
    toast.info("Cobrança cancelada");
  };

  const printReceipt = () => {
    if (!lastSale?.id) return;
    // Impressão via HTML + window.print() (roda dentro do clique do usuário)
    printReceipt80mm(lastSale, storeInfo);
  };

  const openDanfe = async () => {
    if (!pendingDanfe) return;
    const { docType, ref, label } = pendingDanfe;
    if (docType === "nfce") {
      // NFC-e: Focus devolve HTML (recibo do consumidor com QR),
      // não PDF. openPdf rejeitaria %PDF; usamos o helper de HTML.
      await openHtmlDoc(
        `/fiscal/danfe/nfce/${ref}`,
        `NFCe-${ref}.html`,
        `Recibo ${label}`
      );
    } else {
      await openPdf(
        `/fiscal/danfe/${docType}/${ref}`,
        `DANFE-${ref}.pdf`,
        `DANFE ${label}`
      );
    }
  };

  const emitFiscal = async (docType) => {
    if (!lastSale?.id) return;
    setEmitting(true);
    const label = docType.toUpperCase();
    try {
      const { data } = await api.post(`/fiscal/emit/${docType}`, {
        sale_id: lastSale.id,
      });
      const ref = data.ref;
      toast.success(`${label} enviada! Aguardando SEFAZ...`);

      // Poll status até 8 tentativas × 2s = ~16s
      let statusData = data;
      let danfeReady = !!data.caminho_danfe;
      for (let i = 0; i < 8 && !danfeReady; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const st = await api.get(`/fiscal/status/${docType}/${ref}`);
          statusData = st.data;
          if (statusData.caminho_danfe) {
            danfeReady = true;
            break;
          }
          if (
            statusData.status === "erro_autorizacao" ||
            statusData.status === "rejeitado" ||
            (statusData.status_sefaz && statusData.status_sefaz !== "100")
          ) {
            break;
          }
        } catch (err) {
          console.error("PDV fiscal status poll:", err);
        }
      }

      if (danfeReady) {
        toast.success(`${label} autorizada pela SEFAZ ✅ — clique em "Imprimir DANFE" para abrir o PDF`);
        // Guarda a referência; o usuário clica no botão dedicado para
        // abrir o PDF (window.open assíncrono é bloqueado por popup blocker).
        setPendingDanfe({ docType, ref, label });
      } else {
        const reason =
          statusData.mensagem_sefaz ||
          statusData.status ||
          "processamento em andamento";
        toast.error(
          `${label} não pôde imprimir agora: ${reason}. ` +
          `Acesse Fiscal → clique 'Atualizar' na linha #${ref?.slice(-6)}.`,
          { duration: 8000 }
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.focus_error?.mensagem ||
            detail?.focus_error?.erros?.[0]?.mensagem ||
            JSON.stringify(detail?.focus_error || detail || "Erro").slice(0, 250);
      toast.error(`Falha ao emitir ${label}: ${msg}`, { duration: 8000 });
    } finally {
      setEmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pdv-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Ponto de Venda</div>
        <h1 className="font-display text-4xl font-bold">PDV</h1>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Products */}
        <div className="lg:col-span-2 space-y-4">
          {/* Leitor de código de barras — autofoco, dispara no Enter */}
          <form onSubmit={handleBarcodeSubmit} className="relative">
            <ScanLine
              size={18}
              className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors ${barcodeIconColor(barcodeFlash)}`}
            />
            <input
              ref={barcodeRef}
              type="text"
              placeholder="Bipe ou digite o código de barras e pressione Enter..."
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              data-testid="pdv-barcode-input"
              className={`w-full pl-10 pr-3 py-3 border-2 rounded-sm text-sm font-mono tracking-wide bg-white transition-all focus:outline-none focus:ring-2 focus:ring-[#2563EB] ${barcodeInputBorder(barcodeFlash)}`}
            />
          </form>

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text" placeholder="Buscar produto pelo nome ou SKU..."
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="pdv-search-input"
              className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto scrollbar-thin pr-2">
            {filtered.map((p) => (
              <button
                key={p.id} onClick={() => add(p)}
                data-testid={`pdv-product-${p.id}`}
                disabled={p.stock <= 0}
                className="bg-white border border-slate-200 p-4 rounded-sm text-left hover-lift disabled:opacity-60 disabled:cursor-not-allowed relative"
              >
                <div className="font-semibold text-sm text-slate-800 line-clamp-2">{p.name}</div>
                <div className="text-xs text-slate-500 mt-1">{p.sku || "—"}</div>
                <div className="mt-2 flex justify-between items-end">
                  <span className="font-display font-bold text-[#2563EB]">{brl(p.sale_price)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-sm font-semibold ${stockBadgeStyle(p)}`}>
                    {p.stock <= 0 ? "ESGOTADO" : `${p.stock} un`}
                  </span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-sm text-slate-500 py-12">
                Nenhum produto encontrado.
              </div>
            )}
          </div>
        </div>

        {/* Cart */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 h-fit sticky top-6">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart size={18} className="text-[#2563EB]" />
            <h3 className="font-display font-bold text-lg">Carrinho</h3>
            <span className="ml-auto text-xs text-slate-500">{cart.length} itens</span>
          </div>

          {cart.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Adicione produtos ao carrinho</div>
          ) : (
            <ul className="space-y-2 mb-4 max-h-72 overflow-y-auto scrollbar-thin">
              {cart.map((i) => (
                <li key={i.product_id} className="border border-slate-100 p-2 rounded-sm">
                  <div className="flex justify-between text-sm font-medium">
                    <span className="line-clamp-1">{i.name}</span>
                    <button onClick={() => removeItem(i.product_id)} data-testid={`pdv-remove-${i.product_id}`} className="text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <div className="flex items-center gap-1">
                      <button onClick={() => changeQty(i.product_id, -1)} className="w-6 h-6 border border-slate-300 rounded-sm flex items-center justify-center"><Minus size={12} /></button>
                      <span className="px-2 text-sm">{i.quantity}</span>
                      <button onClick={() => changeQty(i.product_id, 1)} className="w-6 h-6 border border-slate-300 rounded-sm flex items-center justify-center"><Plus size={12} /></button>
                    </div>
                    <span className="font-semibold text-sm">{brl(i.unit_price * i.quantity)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-2 text-sm border-t border-slate-200 pt-3">
            <input type="text" placeholder="Nome do cliente"
              value={customerName} onChange={(e) => setCustomerName(e.target.value)}
              data-testid="pdv-customer-name"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />

            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
              data-testid="pdv-employee-select"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
              <option value="">Vendedor (opcional)</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>

            <select value={payment} onChange={(e) => setPayment(e.target.value)}
              data-testid="pdv-payment-select"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
              <option value="dinheiro">Dinheiro</option>
              <option value="pix">Pix</option>
              <option value="cartao_credito">Cartão Crédito</option>
              <option value="cartao_debito">Cartão Débito</option>
              <option value="boleto">Boleto</option>
              <option value="infinitepay">InfinitePay (maquininha / QR)</option>
            </select>

            {coupon ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-300 rounded-sm px-2 py-1.5 text-xs" data-testid="pdv-coupon-applied">
                <span className="text-green-800 font-semibold">🎁 {coupon.code} · {coupon.percent}% OFF · {coupon.customer_name}</span>
                <button onClick={removeCoupon} data-testid="pdv-coupon-remove" className="text-red-600 hover:underline">remover</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()} placeholder="Cupom (ex: NIVER-JOAO-4F2A)"
                  data-testid="pdv-coupon-input" className="flex-1 px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono" />
                <button onClick={applyCoupon} data-testid="pdv-coupon-apply"
                  className="text-xs bg-[#0A1628] hover:bg-[#1e3a5f] text-white px-3 py-1.5 rounded-sm font-semibold">Aplicar</button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-slate-600 text-xs">Desconto</span>
              <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)}
                data-testid="pdv-discount-input"
                className="flex-1 px-2 py-1 border border-slate-300 rounded-sm text-sm" />
            </div>

            <div className="flex justify-between pt-2 text-sm text-slate-600">
              <span>Subtotal</span><span>{brl(subtotal)}</span>
            </div>
            <div className="flex justify-between font-display font-bold text-2xl">
              <span>Total</span><span className="text-[#2563EB]">{brl(total)}</span>
            </div>

            <button onClick={finalize}
              data-testid="pdv-finalize-btn"
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold py-3 rounded-sm mt-3">
              {payment === "infinitepay" ? "Gerar Link InfinitePay" : "Finalizar Venda"}
            </button>
          </div>
        </div>
      </div>

      {/* InfinitePay Checkout Modal */}
      {ipCheckout && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-md w-full p-6" data-testid="ip-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-emerald-600 font-semibold">InfinitePay</div>
                <h2 className="font-display text-xl font-bold">Aguardando Pagamento</h2>
              </div>
              <button onClick={cancelIpCheckout} data-testid="ip-cancel"><X size={20} /></button>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 mb-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <Loader className="animate-spin" size={14} />
                <span className="text-sm font-semibold">Polling a cada 3s — vai confirmar automaticamente</span>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(ipCheckout.checkout_url)}`}
                alt="QR Code"
                className="mx-auto mb-3"
                data-testid="ip-qrcode"
              />
              <div className="text-xs text-slate-500">O cliente escaneia, paga e o sistema confirma sozinho.</div>
            </div>
            <div className="space-y-2">
              <a href={ipCheckout.checkout_url} target="_blank" rel="noreferrer"
                data-testid="ip-open-link"
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-sm text-sm font-semibold">
                <ExternalLink size={14} /> Abrir página de pagamento
              </a>
              <button
                onClick={() => { navigator.clipboard.writeText(ipCheckout.checkout_url); toast.success("Link copiado!"); }}
                data-testid="ip-copy-link"
                className="w-full flex items-center justify-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-sm text-sm font-semibold">
                <Copy size={14} /> Copiar link
              </button>
              <div className="text-xs text-slate-500 text-center pt-2">
                Valor: <strong>{brl(ipCheckout.total)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Post-sale actions: print receipt / emit fiscal */}
      {lastSale && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="post-sale-modal"
        >
          <div className="bg-white rounded-sm max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-emerald-600 font-semibold">
                  Venda Concluída
                </div>
                <h2 className="font-display text-2xl font-bold">
                  {brl(lastSale.total)}
                </h2>
                <div className="text-xs text-slate-500 mt-1">
                  Venda #{lastSale.id.slice(0, 8).toUpperCase()} —{" "}
                  {lastSale.customer_name || "Consumidor"}
                </div>
              </div>
              <button
                onClick={() => { setLastSale(null); setPendingDanfe(null); }}
                data-testid="post-sale-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-2">
              <button
                onClick={printReceipt}
                data-testid="post-sale-print-receipt"
                className="w-full flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2.5 rounded-sm text-sm font-semibold"
              >
                <Receipt size={14} /> Imprimir Cupom (80mm)
              </button>
              {pendingDanfe && (
                <button
                  onClick={openDanfe}
                  data-testid="post-sale-print-danfe"
                  className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2.5 rounded-sm text-sm font-semibold animate-pulse"
                >
                  <Printer size={14} /> Imprimir DANFE {pendingDanfe.label}
                </button>
              )}
              <button
                onClick={() => emitFiscal("nfce")}
                disabled={emitting}
                data-testid="post-sale-emit-nfce"
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-sm text-sm font-semibold"
              >
                {emitting ? (
                  <Loader className="animate-spin" size={14} />
                ) : (
                  <FileText size={14} />
                )}
                Emitir NFC-e (consumidor)
              </button>
              <button
                onClick={() => emitFiscal("nfe")}
                disabled={emitting}
                data-testid="post-sale-emit-nfe"
                className="w-full flex items-center justify-center gap-2 border-2 border-[#0A1628] hover:bg-[#0A1628] hover:text-white text-[#0A1628] px-4 py-2.5 rounded-sm text-sm font-semibold disabled:opacity-50"
              >
                {emitting ? (
                  <Loader className="animate-spin" size={14} />
                ) : (
                  <FileText size={14} />
                )}
                Emitir NF-e (com CPF/CNPJ)
              </button>
              <button
                onClick={() => { setLastSale(null); setPendingDanfe(null); }}
                className="w-full text-slate-500 hover:text-slate-700 text-xs py-2"
              >
                Pular
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
